import { IPromise, IScope } from "angular";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { BadgeEvents } from "@gtmhub/badges/badgeEvents";
import { IRestLayerRequest } from "@gtmhub/core";
import { IStateInit } from "@gtmhub/core/routing";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { ApmSpanService } from "@gtmhub/core/tracing/apm-span.service";
import { IIndicator } from "@gtmhub/error-handling";
import { localize } from "@gtmhub/localization";
import {
  GridFilterDefinitions,
  GridSortingDefinitions,
  IGridFilterDefinition,
  IGridFiltersChange,
  IGridSortingRule,
  OrFiltersGroup,
  createFilter,
  getPersistedScreenSettings,
  setPersistedScreenSettings,
  toGridDefinitionMap,
  toRestLayerRequest,
} from "@gtmhub/shared/components/grid-filtering-bar";
import { SnackbarEvents } from "@gtmhub/shared/components/ui/snackbar/snackbar.events";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { scrollToTop } from "@gtmhub/shared/utils/dom-utils";
import { INgRedux } from "@gtmhub/state-management";
import { ITeamsStoreState, TeamsActions } from "@gtmhub/teams";
import { ITeamDeletedArgs, ITeamEventArgs, TeamEvents } from "@gtmhub/teams/events";
import { IdMap, capitalize } from "@gtmhub/util";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { IReduxTeam, ITeam } from "../models/models";
import { TeamService } from "../team.service";

export interface ITeamsScope extends IExtendedRootScopeService, IScopeFromRedux, IScope {
  teams: ITeam[];
  indicators: {
    loading?: IIndicator;
    updating?: IIndicator;
  };
  currentPage: number;
  totalPages: number;
  onPageChange(page: number): void;
  filterDefinitions: GridFilterDefinitions;
  filters: OrFiltersGroup[];
  sortingDefinitions: GridSortingDefinitions;
  sortingRules: IGridSortingRule[];
  filtersOrSortingChange(change?: IGridFiltersChange): void;
}
interface IScopeFromRedux {
  teamsIdMap: IdMap<IReduxTeam>;
  teamsFetched: boolean;
  assigneesIdMap: IdMap<Assignee>;
  assigneesFetched: boolean;
  noTeamsInAccount: boolean;
}

const bindStateToScope = (state: ITeamsStoreState & IAssigneesStoreState): IScopeFromRedux => {
  return {
    noTeamsInAccount: state.teams.isFetched && state.teams.items.length === 0,
    teamsIdMap: state.teams.map,
    teamsFetched: state.teams.isFetched,
    assigneesIdMap: state.assignees.map,
    assigneesFetched: state.assignees.isFetched,
  };
};
const TEAMS_PER_PAGE = 15;
const SCREEN_PATH = ["teams"];

export class TeamsCtrl implements IStateInit {
  private teamEvents: TeamEvents;
  private badgeEvents: BadgeEvents;
  private snackbarEvents: SnackbarEvents;

  public static $inject = ["$scope", "$rootScope", "TeamService", "$ngRedux", "TeamsActions", "AssigneeActions", "ApmSpanService"];

  constructor(
    private $scope: ITeamsScope,
    private $rootScope: ITraceRootScopeService,
    private teamService: TeamService,
    private $ngRedux: INgRedux,
    private teamsActions: TeamsActions,
    private assigneeActions: AssigneeActions,
    private apmSpanService: ApmSpanService
  ) {
    this.$scope.indicators = {};

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("accountTeamsUpdatedV2", this.onTeamsChanged);

    this.$scope.$on("$destroy", unsubscribe);

    this.teamEvents = new TeamEvents($scope);
    this.badgeEvents = new BadgeEvents($scope);
    this.snackbarEvents = new SnackbarEvents($scope);

    // several scenarios are covered and explained with comments in handler
    this.teamEvents.onTeamDeleted((args) => this.onTeamDeleted(args));

    // we want to fetch filtered partial teams on team created because the new team may match or not filters applied
    this.teamEvents.onTeamCreated((args) => this.teamCreated(args));

    // getting badge assignments so the changes may be applied properly to team list displayed
    this.badgeEvents.onBadgeAwardChanged(() => this.onTeamsChanged());
  }

  public stateInit(): IPromise<unknown> {
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());

    this.initGridFilteringBar();

    return this.apmSpanService.dataLoadSpan("teams-init", () => this.initTeamsList());
  }

  private initTeamsList = (): IPromise<unknown> => {
    this.$scope.currentPage = 1;
    this.$scope.onPageChange = this.onPageChange;

    this.$scope.indicators.loading = { progress: true };
    return this.getTeams()
      .then(() => delete this.$scope.indicators.loading)
      .catch((error) => (this.$scope.indicators.loading = { error }));
  };

  private initGridFilteringBar = () => {
    const filterDefinitions: IGridFilterDefinition[] = [
      {
        dataType: "text",
        fieldName: "name",
        displayName: localize("name_cap"),
        trackingName: "Name",
        defaultOperator: "contains",
        supportedOperators: ["equals", "notEqual", "contains", "doesNotContain"],
      },
      {
        dataType: "boolean",
        fieldName: "isActive",
        displayName: localize("status"),
        trackingName: "Status",
        applyFirstOnCreate: true,
        inferValueFromOperator: true,
        entries: [
          // the `isActive` field can be `true/false/undefined` => `undefined` is implicitly interpreted as `true`,
          // so use `equals: false` and `notEqual: false` instead of `equals: true` and `equals: false`
          { operator: "notEqual", value: false, displayName: localize("active") },
          { operator: "equals", value: false, displayName: capitalize(localize("deactivated")) },
        ],
        unique: true,
      },
    ];
    this.$scope.filterDefinitions = toGridDefinitionMap(filterDefinitions);
    this.$scope.filters = getPersistedScreenSettings(SCREEN_PATH, "filters") || [[createFilter(this.$scope.filterDefinitions["name"])]];

    this.$scope.sortingDefinitions = toGridDefinitionMap([
      {
        fieldName: "name",
        displayName: localize("name_cap"),
        trackingName: "Name",
      },
      {
        fieldName: "members",
        displayName: localize("members_count"),
        trackingName: "Members",
      },
    ]);
    this.$scope.sortingRules = getPersistedScreenSettings(SCREEN_PATH, "sorting") || [{ fieldName: "name", reversed: false }];

    this.$scope.filtersOrSortingChange = this.filtersOrSortingChange;
  };

  private filtersOrSortingChange = (change?: IGridFiltersChange) => {
    if (change?.initial) {
      return;
    }

    setPersistedScreenSettings(SCREEN_PATH, "filters", this.$scope.filters);
    setPersistedScreenSettings(SCREEN_PATH, "sorting", this.$scope.sortingRules);

    this.$scope.currentPage = 1;
    let indicatorInProgress: keyof ITeamsScope["indicators"];
    if (this.$scope.indicators.loading) {
      indicatorInProgress = "loading";
    } else {
      this.$scope.indicators.updating = { progress: true };
      indicatorInProgress = "updating";
    }

    this.getTeams()
      .then(() => delete this.$scope.indicators[indicatorInProgress])
      .catch((error) => (this.$scope.indicators[indicatorInProgress] = { error }));
  };

  private teamCreated = (args: ITeamEventArgs) => {
    this.snackbarEvents.broadcastShowSnackbar("create_team_success", { inputPlaceholders: { teamName: args.team.name } });
  };

  private onTeamsChanged = () => {
    this.$scope.indicators.updating = { progress: true };
    this.getTeams()
      .then(() => delete this.$scope.indicators.updating)
      .catch((error) => (this.$scope.indicators.updating = { error }));
  };

  private onTeamDeleted = (args: ITeamDeletedArgs): void => {
    // if there are 2 or more pages and the team is the only one listed on the last page
    if (this.$scope.currentPage > 1 && this.$scope.teams.length === 1) {
      // go to the previous page as the team should be removed from the last page and no results will be listed there
      this.onPageChange(this.$scope.currentPage - 1);
      // if the team deleted is on the last page
    } else if (this.$scope.currentPage === this.$scope.totalPages) {
      // just remove it from collection
      this.$scope.teams = this.$scope.teams.filter((team) => team.id !== args.teamId);
    } else {
      // in all other cases - fetch teams again to keep page items count to its limit
      this.onTeamsChanged();
    }
  };

  private getTeams = () => {
    const request = this.createTeamsRequest();

    return this.fetchTeams(request);
  };

  private fetchTeams = (params: IRestLayerRequest) => {
    return this.teamService.getTeams(params).then((teams) => {
      this.$scope.teams = teams.items.map((team) => ({
        ...this.$scope.teamsIdMap?.[team.id],
        ...this.$scope.assigneesIdMap?.[team.id],
        type: "team", // needed for the ui-assignee-avatar
        ...team,
      })) as ITeam[];

      this.$scope.totalPages = Math.ceil(teams.totalCount / TEAMS_PER_PAGE);

      // totalPages equals 0 when no teams are created yet
      if (this.$scope.totalPages && this.$scope.currentPage > this.$scope.totalPages) {
        this.onPageChange(this.$scope.totalPages);
      }
    });
  };

  private createTeamsRequest = (): IRestLayerRequest => {
    const request = toRestLayerRequest({ filters: this.$scope.filters, sorting: this.$scope.sortingRules });
    request.skip = (this.$scope.currentPage - 1) * TEAMS_PER_PAGE;
    request.limit = TEAMS_PER_PAGE;
    request.fields = "id,description,badges";

    // fetch teams redux data for the current batch, if not yet loaded
    if (!this.$scope.teamsFetched) {
      request.fields += ",manager,members";
    }

    // fetch assignees redux data for the current batch, if not yet loaded
    if (!this.$scope.assigneesFetched) {
      request.fields += ",name,isActive,avatar,color,picture";
    }

    return request;
  };

  private onPageChange = (page: number) => {
    this.$rootScope.traceAction("page_changed", () => {
      this.$scope.currentPage = page;

      this.$scope.indicators.updating = { progress: true };
      this.getTeams()
        .then(() => {
          delete this.$scope.indicators.updating;
          scrollToTop(".master-container");
        })
        .catch((error) => (this.$scope.indicators.updating = { error }));
    });
  };
}
